import React, { useEffect, useState, useCallback } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import Typography from '@tiptap/extension-typography'
import Placeholder from '@tiptap/extension-placeholder'
import Underline from '@tiptap/extension-underline'
import CodeBlock from '@tiptap/extension-code-block'
import { TextStyle } from '@tiptap/extension-text-style'
import { FontSize } from './FontSizeExtension'
import EditorToolbar from './EditorToolbar'
import AutoSaveIndicator from './AutoSaveIndicator'

// Enhanced TipTap editor with Slack-like features:
// - Markdown shortcuts (*, -, #, **, `, etc.)
// - Smart typography (quotes, dashes, ellipsis)
// - Code blocks and inline code
// - Underline support

/**
 * Rich text editor component using TipTap
 * Features:
 * - WYSIWYG editing with formatting toolbar
 * - GM-only notes with // syntax (like code comments)
 * - Auto-save with visual indicators
 * - Image support (inline)
 * - Link support
 */
const RichTextEditor = ({
  content = '',
  onChange,
  onSave,
  isGM = false,
  placeholder = 'Start writing your session notes...',
  autoSave = true,
  autoSaveDelay = 30000, // 30 seconds
}) => {
  const [saveStatus, setSaveStatus] = useState('idle')
  const [lastSavedTime, setLastSavedTime] = useState(null)
  const [isSaving, setIsSaving] = useState(false)

  // Configure TipTap editor with ALL the modern features
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Configure built-in extensions
        heading: {
          levels: [1, 2, 3],
        },
        codeBlock: false, // Disable StarterKit's code block (we'll use our own)
      }),

      // Typography - Smart markdown shortcuts
      Typography.configure({
        // Markdown shortcuts like Slack:
        // Type * or - followed by space → bullet list
        // Type 1. followed by space → numbered list
        // Type # followed by space → heading
        // Type ``` followed by space → code block
      }),

      // Placeholder - Better placeholder text
      Placeholder.configure({
        placeholder: ({ node }) => {
          if (node.type.name === 'heading') {
            return `Heading ${node.attrs.level}`
          }
          if (node.type.name === 'codeBlock') {
            return 'Enter code...'
          }
          return placeholder
        },
        showOnlyWhenEditable: true,
        showOnlyCurrent: true,
      }),

      // Code blocks with syntax highlighting potential
      CodeBlock.configure({
        HTMLAttributes: {
          class: 'bg-gray-800 text-gray-100 p-3 rounded font-mono text-sm',
        },
      }),

      // Underline support
      Underline,

      // Text styling (required for fontSize)
      TextStyle,

      // Font size control
      FontSize,

      // Images
      Image.configure({
        inline: true,
        allowBase64: false,
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded',
        },
      }),

      // Links with auto-detection
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        HTMLAttributes: {
          class: 'text-blue-500 underline hover:text-blue-600 cursor-pointer',
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
    ],
    content,
    editable: isGM, // Controls editability (pass true to enable editing)
    editorProps: {
      attributes: {
        class:
          'prose prose-sm prose-invert max-w-none p-4 min-h-[400px] focus:outline-none bg-gray-900 text-white rounded-b prose-ul:list-disc prose-ul:pl-6 prose-ol:list-decimal prose-ol:pl-6 prose-li:my-1',
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML()
      if (onChange) {
        onChange(html)
      }
    },
  })

  // Auto-save implementation
  const saveContent = useCallback(async () => {
    if (!editor || !onSave || isSaving) return

    try {
      setIsSaving(true)
      setSaveStatus('saving')

      const html = editor.getHTML()
      await onSave(html)

      setSaveStatus('saved')
      setLastSavedTime(new Date())

      // Reset to idle after 3 seconds
      setTimeout(() => {
        setSaveStatus('idle')
      }, 3000)
    } catch (error) {
      console.error('Auto-save failed:', error)
      setSaveStatus('error')

      // Retry after 5 seconds
      setTimeout(() => {
        saveContent()
      }, 5000)
    } finally {
      setIsSaving(false)
    }
  }, [editor, onSave, isSaving])

  // Debounced auto-save
  useEffect(() => {
    if (!editor || !autoSave || !isGM) return

    let timeoutId

    const handleUpdate = () => {
      // Clear existing timeout
      if (timeoutId) {
        clearTimeout(timeoutId)
      }

      // Set new timeout
      timeoutId = setTimeout(() => {
        saveContent()
      }, autoSaveDelay)
    }

    editor.on('update', handleUpdate)

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
      editor.off('update', handleUpdate)
    }
  }, [editor, autoSave, isGM, autoSaveDelay, saveContent])

  // Update editor content when prop changes
  useEffect(() => {
    if (!editor || editor.getHTML() === content) return

    editor.commands.setContent(content)
  }, [editor, content])

  if (!editor) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-400">Loading editor...</div>
      </div>
    )
  }

  return (
    <div className="rich-text-editor">
      <div className="border border-gray-700 rounded">
        {/* Toolbar */}
        <EditorToolbar editor={editor} />

      {/* Editor content */}
      <div className="relative">
        <style>{`
          /* Lists */
          .ProseMirror ul {
            list-style-type: disc;
            padding-left: 1.5rem;
            margin: 0.5rem 0;
          }
          .ProseMirror ol {
            list-style-type: decimal;
            padding-left: 1.5rem;
            margin: 0.5rem 0;
          }
          .ProseMirror li {
            margin: 0.25rem 0;
          }
          .ProseMirror li p {
            margin: 0;
          }
          .ProseMirror ul ul,
          .ProseMirror ol ul {
            list-style-type: circle;
          }
          .ProseMirror ol ol,
          .ProseMirror ul ol {
            list-style-type: lower-alpha;
          }

          /* Headings */
          .ProseMirror h1 {
            font-size: 2em;
            font-weight: bold;
            margin: 1rem 0 0.5rem 0;
            line-height: 1.2;
          }
          .ProseMirror h2 {
            font-size: 1.5em;
            font-weight: bold;
            margin: 0.75rem 0 0.5rem 0;
            line-height: 1.3;
          }
          .ProseMirror h3 {
            font-size: 1.25em;
            font-weight: bold;
            margin: 0.5rem 0 0.25rem 0;
            line-height: 1.4;
          }

          /* Paragraphs */
          .ProseMirror p {
            margin: 0.5rem 0;
          }

          /* Code */
          .ProseMirror code {
            background-color: rgba(110, 118, 129, 0.4);
            border-radius: 3px;
            font-size: 0.9em;
            padding: 0.2em 0.4em;
            font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          }

          /* Code Blocks */
          .ProseMirror pre {
            background: #1e1e1e;
            border-radius: 0.5rem;
            color: #fff;
            font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
            padding: 0.75rem 1rem;
            margin: 1rem 0;
          }
          .ProseMirror pre code {
            background: none;
            padding: 0;
            font-size: inherit;
          }

          /* Blockquotes */
          .ProseMirror blockquote {
            border-left: 3px solid #6b7280;
            padding-left: 1rem;
            margin: 1rem 0;
            font-style: italic;
            color: #9ca3af;
          }

          /* Horizontal Rule */
          .ProseMirror hr {
            border: none;
            border-top: 2px solid #4b5563;
            margin: 2rem 0;
          }

          /* Links */
          .ProseMirror a {
            color: #3b82f6;
            text-decoration: underline;
            cursor: pointer;
          }
          .ProseMirror a:hover {
            color: #60a5fa;
          }

          /* Strong & Em */
          .ProseMirror strong {
            font-weight: bold;
          }
          .ProseMirror em {
            font-style: italic;
          }
          .ProseMirror u {
            text-decoration: underline;
          }
          .ProseMirror s {
            text-decoration: line-through;
          }
        `}</style>
        <EditorContent editor={editor} />

        {/* Placeholder (when empty) */}
        {editor.isEmpty && (
          <div className="absolute top-4 left-4 text-gray-500 pointer-events-none">
            {placeholder}
          </div>
        )}
      </div>

      {/* Auto-save indicator */}
      {autoSave && (
        <div className="border-t border-gray-700 bg-gray-800">
          <AutoSaveIndicator status={saveStatus} lastSavedTime={lastSavedTime} />
        </div>
      )}
      </div>
    </div>
  )
}

export default RichTextEditor
